import Slider from "react-slick";
import { Link } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { blogArticles as defaultArticles, blogCategories as defaultCategories } from "../../data/blogContent";
import { resolveArticleCover } from "../../utils/articleCoverImage";

const NextArrow = ({ onClick, ariaLabel = "Siguiente artículo" }) => (
  <button
    type="button"
    className="absolute top-1/2 -right-12 z-10 hidden lg:flex -translate-y-1/2 items-center justify-center h-14 w-14 rounded-full bg-white shadow-lg border border-blue-100 hover:bg-blue-600 hover:text-white transition-colors duration-200"
    onClick={onClick}
    aria-label={ariaLabel}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M9.29 6.71a1 1 0 011.42 0L15 11l-4.29 4.29a1 1 0 11-1.42-1.42L12.17 12l-2.88-2.88a1 1 0 010-1.41z" />
    </svg>
  </button>
);

const PrevArrow = ({ onClick, ariaLabel = "Artículo anterior" }) => (
  <button
    type="button"
    className="absolute top-1/2 -left-12 z-10 hidden lg:flex -translate-y-1/2 items-center justify-center h-14 w-14 rounded-full bg-white shadow-lg border border-blue-100 hover:bg-blue-600 hover:text-white transition-colors duration-200"
    onClick={onClick}
    aria-label={ariaLabel}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14.71 6.71a1 1 0 010 1.41L11.83 12l2.88 2.88a1 1 0 01-1.42 1.42L9 12l4.29-4.29a1 1 0 011.42 0z" />
    </svg>
  </button>
);

const BlogSlider = ({
  items,
  basePath = "/blog",
  categories = defaultCategories,
  articles = defaultArticles,
  badgeLabel = "Selección especial",
  titleLabel = "Artículos que están dando de qué hablar",
  readArticleLabel = "Leer artículo",
  prevAriaLabel = "Artículo anterior",
  nextAriaLabel = "Siguiente artículo",
}) => {
  const sliderItems =
    items && items.length > 0
      ? items
      : articles.filter((article) => article.featured).slice(0, 6);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <NextArrow ariaLabel={nextAriaLabel} />,
    prevArrow: <PrevArrow ariaLabel={prevAriaLabel} />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
        },
      },
    ],
  };

  return (
    <div className="w-full px-4 sm:px-8 lg:px-24 bg-[#f4f9fb] py-14 sm:py-20 flex justify-center overflow-hidden">
      <div className="max-w-7xl w-full relative">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 text-center sm:text-left">
          <div>
            <p className="text-xs sm:text-sm font-semibold text-[#1746a2] uppercase tracking-[0.3em]">
              {badgeLabel}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#102a72] mt-2 leading-tight">
              {titleLabel}
            </h2>
          </div>
        </div>
        <Slider
          {...settings}
          className="blog-featured-slider [&_.slick-track]:flex [&_.slick-track]:justify-center [&_.slick-slide]:px-3 sm:[&_.slick-slide]:px-6 [&_.slick-slide]:py-8 sm:[&_.slick-slide]:py-10 [&_.slick-slide]:h-auto [&_.slick-list]:overflow-visible sm:[&_.slick-list]:overflow-hidden px-2 sm:px-6"
        >
          {sliderItems.map((article) => {
            const category = categories[article.category];
            const cover = resolveArticleCover(article, categories);
            return (
              <div key={article.slug} className="h-full">
                <article className="bg-white rounded-3xl shadow-md hover:shadow-[0_26px_46px_rgba(15,26,73,0.22)] hover:-translate-y-1.5 transition-all duration-300 border border-blue-100 overflow-hidden flex flex-col h-full min-h-[460px] sm:min-h-[520px] max-w-[360px] w-full mx-auto">
                  <div
                    className="h-48 sm:h-56 bg-cover bg-center rounded-t-3xl"
                    style={{ backgroundImage: `url(${cover.src})` }}
                    role="img"
                    aria-label={cover.alt}
                  />
                  <div className="p-6 flex flex-col gap-4 flex-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-[#1746a2]">
                      {category?.title} • {article.date}
                    </span>
                    <h3 className="text-xl font-bold text-[#123070] leading-tight">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="mt-auto flex justify-between items-center text-xs text-gray-500 font-medium">
                      <span>{article.author}</span>
                      <span>{article.readTime}</span>
                    </div>
                    <Link
                      to={`${basePath}/${article.category}/${article.slug}`}
                      className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-900 transition-colors duration-150 text-sm mt-2"
                    >
                      {readArticleLabel}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </div>
                </article>
              </div>
            );
          })}
        </Slider>
      </div>
    </div>
  );
};

export default BlogSlider;
